import { createContext, useCallback, useContext, useEffect, useState, PropsWithChildren, useRef, useMemo } from 'react';
import { usePostHog } from '@posthog/react';
import supabaseClient from '@/api/supabaseClient.ts';
import { Session, SupabaseClient } from '@supabase/supabase-js';
import useLogin from '@/api/useLogin.ts';
import useSendResetPasswordLink from '@/api/useSendResetPasswordLink.ts';
import useResetPassword from '@/api/useResetPassword.ts';
import useVerifyEmail from '@/api/useVerifyEmail.ts';
import useResendVerificationCode from '@/api/useResendVerificationCode.ts';
import useUser from '@/api/useUser.ts';
import { User } from '$/models/User';
import OnboardingStep from '$/_const/OnboardingStep.ts';
import useSubscriptions from '@/api/useSubscriptions.ts';
import useProducts from '@/api/useProducts.ts';
import type { SubscriptionWithProduct } from '$/dto/SubscriptionWithProduct.d.ts';
import dayjs from '$/utils/dayjs';
import ProductType from '$/_const/ProductType.ts';
import Routes from '@/router/Routes.ts';
import type { AvailableRoutes } from '@/router/Routes.ts';
import { passToRouteSearchParams } from '@/utils/passToRouteSearchParams.ts';
import SearchParams from '@/_constants/SearchParams.ts';

interface AuthContextProps {
  session: Session | null;
  user: User | null;
  isOnboarding: boolean;
  supabaseClient: SupabaseClient;
  logout: () => void;
  isLoading: boolean;
  loginQuery: ReturnType<typeof useLogin>;
  sendResetPasswordLinkQuery: ReturnType<typeof useSendResetPasswordLink>;
  resetPasswordQuery: ReturnType<typeof useResetPassword>;
  verifyEmailQuery: ReturnType<typeof useVerifyEmail>;
  resendVerificationCodeQuery: ReturnType<typeof useResendVerificationCode>;
  subscription: SubscriptionWithProduct | null;
  isSubscriptionActive: boolean;
  isTrial: boolean;
  subscriptionDaysLeft: number;
  canCreateTrip: boolean;
  canRefineTrip: (currentRefinements: number) => boolean;
  getSubscriptionPage: () => AvailableRoutes;
  isCancelAtPeriodEnd: boolean;
  canUpgrade: boolean;
}

const AuthContext = createContext<AuthContextProps>(undefined!);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const isPendingAuthChangeCheck = useRef(false);
  const identifiedUserId = useRef<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const { data: user, isLoading: isLoadingUserData } = useUser(session?.user?.id);

  const { data: subscriptions, isLoading: isLoadingSubscriptions } = useSubscriptions(!!user, { user_id: user?.id });

  const subscription = useMemo(() => {
    return subscriptions?.[0] ?? null;
  }, [subscriptions]);

  const { data: products, isLoading: isLoadingProducts } = useProducts(!!subscription, {
    price_id: subscription?.price_id,
  });

  const product = useMemo(() => {
    return products?.[0] ?? null;
  }, [products]);

  const subscriptionWithProduct: SubscriptionWithProduct | null = useMemo(() => {
    if (!subscription || !product) return null;

    return {
      ...subscription,
      product,
    };
  }, [subscription, product]);

  const isSubscriptionActive = subscriptionWithProduct?.current_period_end
    ? dayjs.utc(subscriptionWithProduct?.current_period_end).isAfter(dayjs.utc())
    : false;

  const isCancelAtPeriodEnd = subscriptionWithProduct?.cancel_at_period_end ?? false;

  const isTrial = subscriptionWithProduct?.product.type === ProductType.TRIAL;

  const canUpgrade =
    !!subscriptionWithProduct?.product &&
    !isTrial &&
    isSubscriptionActive &&
    subscriptionWithProduct?.product?.interval_count < 12;

  const timeLeftInDays = subscriptionWithProduct?.current_period_end
    ? Math.ceil(dayjs.utc(subscriptionWithProduct!.current_period_end).diff(dayjs.utc(), 'hour') / 24)
    : 0;

  const subscriptionDaysLeft = isSubscriptionActive ? (timeLeftInDays > 0 ? timeLeftInDays : 0) : 0;

  const canCreateTrip = !!user && user.user_features.trips > 0;

  const canRefineTrip = useCallback(
    (currentRefinements: number) => {
      return !!user && user.user_features.refinements_per_trip > currentRefinements;
    },
    [user]
  );

  const getSubscriptionPage = useCallback(() => {
    return passToRouteSearchParams(Routes.SUBSCRIBE, {
      [SearchParams.REDIRECT_URL]: encodeURIComponent(
        `${window.location.origin}${passToRouteSearchParams(Routes.AFTER_PAYMENT_LANDING, {
          [SearchParams.REDIRECT_URL]: encodeURIComponent(window.location.href),
        })}`
      ),
    }) as AvailableRoutes;
  }, []);

  const isOnboarding = !!user && user?.onboarding_step !== OnboardingStep.FINISHED;

  const posthog = usePostHog();

  useEffect(() => {
    const userId = session?.user.id;

    if (!posthog || !userId || !user || identifiedUserId.current === userId) return;

    if (identifiedUserId.current) {
      posthog.reset();
    }

    posthog.identify(userId, {
      email: session.user.email,
      name: user.name,
      preferred_name: user.preferred_name,
    });
    identifiedUserId.current = userId;
  }, [session, user, posthog]);

  const logout = useCallback(() => {
    posthog?.capture('user_logged_out');
    posthog?.reset();
    identifiedUserId.current = null;
    void supabaseClient.auth.signOut();
  }, [posthog]);

  const loginQuery = useLogin();

  const sendResetPasswordLinkQuery = useSendResetPasswordLink();

  const resetPasswordQuery = useResetPassword();

  const verifyEmailQuery = useVerifyEmail();

  const resendVerificationCodeQuery = useResendVerificationCode();

  const checkData = useCallback(async (session: Session | null) => {
    if (isPendingAuthChangeCheck.current) return;

    isPendingAuthChangeCheck.current = true;

    if (!session) {
      setIsLoading(false);
      setSession(null);
      isPendingAuthChangeCheck.current = false;
      return;
    }

    setIsLoading(false);
    setSession(session);
    isPendingAuthChangeCheck.current = false;
  }, []);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabaseClient.auth.onAuthStateChange((_event, session) => {
      checkData(session);
    });

    return () => {
      subscription.unsubscribe();
      setIsLoading(true);
      setSession(null);
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        session,
        user: user ?? null,
        isOnboarding,
        supabaseClient,
        logout,
        isLoading: isLoading || isLoadingUserData || isLoadingSubscriptions || isLoadingProducts,
        loginQuery,
        resetPasswordQuery,
        sendResetPasswordLinkQuery,
        verifyEmailQuery,
        resendVerificationCodeQuery,
        subscription: subscriptionWithProduct,
        isSubscriptionActive,
        isTrial,
        subscriptionDaysLeft,
        canCreateTrip,
        canRefineTrip,
        getSubscriptionPage,
        isCancelAtPeriodEnd,
        canUpgrade,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuthContext(): AuthContextProps {
  const context = useContext(AuthContext);

  if (typeof context === 'undefined') {
    throw new Error('useAuthContext should be used within the AuthContext provider!');
  }

  return context;
}
